// =============================================
// ENHANCED EXPORT SETTINGS MODAL
// components/reports/ExportSettingsModal.js
// =============================================

import React, { useState, useEffect } from 'react'
import { X, Upload, Plus, Trash2, Save, Eye, Palette, Building, FileText, Shield, Calendar } from 'lucide-react'
import { supabase } from '../../lib/supabase'

export const ExportSettingsModal = ({ isOpen, onClose, currentSettings, onSave, currentTenant }) => {
  const [settings, setSettings] = useState({
    template: 'professional',
    branding: {
      companyName: '',
      logo: '',
      phone: '',
      email: '',
      website: '',
      address: '',
      primaryColor: '#3B82F6',
      accentColor: '#10B981'
    },
    confidentiality: {
      enabled: true,
      level: 'confidential',
      customText: '',
      showWatermark: false,
      includeDisclaimer: true
    },
    dateRange: {
      showOnCover: true,
      format: 'full', // full, short, custom
      customFormat: 'MMMM DD, YYYY'
    },
    customSections: []
  })

  const [activeTab, setActiveTab] = useState('template')
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(false)

  const templates = [
    {
      id: 'professional',
      name: 'Professional',
      description: 'Clean, corporate layout with emphasis on data and analytics',
      preview: '/api/placeholder/300/200',
      features: ['Header with logo', 'Executive summary', 'Clean tables', 'Charts & graphs'],
      color: '#3B82F6'
    },
    {
      id: 'modern',
      name: 'Modern',
      description: 'Contemporary design with bold visuals and interactive elements',
      preview: '/api/placeholder/300/200',
      features: ['Bold typography', 'Color-coded sections', 'Visual metrics', 'Modern charts'],
      color: '#10B981'
    },
    {
      id: 'minimal',
      name: 'Minimal',
      description: 'Simple, clean layout focusing on essential information only',
      preview: '/api/placeholder/300/200',
      features: ['Minimal design', 'Essential data only', 'Clean typography', 'Focused content'],
      color: '#6B7280'
    },
    {
      id: 'detailed',
      name: 'Detailed',
      description: 'Comprehensive layout with extensive data breakdowns',
      preview: '/api/placeholder/300/200',
      features: ['Detailed breakdowns', 'Multiple data views', 'Rich analytics', 'Full reports'],
      color: '#8B5CF6'
    },
    {
      id: 'executive',
      name: 'Executive',
      description: 'High-level overview perfect for C-suite presentations',
      preview: '/api/placeholder/300/200',
      features: ['Executive summary', 'Key metrics focus', 'Strategic insights', 'High-level view'],
      color: '#DC2626'
    },
    {
      id: 'client',
      name: 'Client Facing',
      description: 'Professional client reports with billing focus',
      preview: '/api/placeholder/300/200',
      features: ['Client branding', 'Billing emphasis', 'Professional tone', 'Invoice ready'],
      color: '#059669'
    }
  ]

  const confidentialityLevels = [
    { value: 'public', label: 'Public', description: 'No confidentiality restrictions' },
    { value: 'internal', label: 'Internal Use', description: 'For internal company use only' },
    { value: 'confidential', label: 'Confidential', description: 'Confidential company information' },
    { value: 'restricted', label: 'Restricted', description: 'Highly restricted access required' }
  ]

  const dateFormats = [
    { value: 'full', label: 'Full Date', example: 'January 15, 2025 - February 28, 2025' },
    { value: 'short', label: 'Short Date', example: '01/15/25 - 02/28/25' },
    { value: 'iso', label: 'ISO Format', example: '2025-01-15 to 2025-02-28' },
    { value: 'custom', label: 'Custom Format', example: 'Define your own format' }
  ]

  const tabs = [
    { id: 'template', label: 'Template', icon: FileText },
    { id: 'branding', label: 'Branding', icon: Building },
    { id: 'legal', label: 'Legal & Privacy', icon: Shield },
    { id: 'sections', label: 'Custom Sections', icon: Plus }
  ]

  useEffect(() => {
    if (currentSettings) {
      setSettings(prev => ({ ...prev, ...currentSettings }))
    }
    if (currentTenant) {
      loadTenantSettings()
    }
  }, [currentSettings, currentTenant])

  const loadTenantSettings = async () => {
    try {
      setLoading(true)
      
      // Use existing tenant table structure
      const { data, error } = await supabase
        .from('tenants')
        .select('report_settings, logo_url, company_address, phone, email, website, primary_color, accent_color, name')
        .eq('id', currentTenant.id)
        .single()

      if (error) {
        console.error('Error loading tenant settings:', error)
        initializeWithTenantData()
        return
      }

      if (data) {
        // Map existing tenant data to our settings format
        const mappedSettings = {
          template: data.report_settings?.template || 'professional',
          branding: {
            companyName: data.name || currentTenant.name,
            logo: data.logo_url || '',
            phone: data.phone || '',
            email: data.email || '',
            website: data.website || '',
            address: data.company_address || '',
            primaryColor: data.primary_color || '#3B82F6',
            accentColor: data.accent_color || '#10B981'
          },
          confidentiality: {
            enabled: data.report_settings?.confidentiality?.enabled ?? true,
            level: data.report_settings?.confidentiality?.level || 'confidential',
            customText: data.report_settings?.confidentiality?.customText || '',
            showWatermark: data.report_settings?.confidentiality?.showWatermark || false,
            includeDisclaimer: data.report_settings?.confidentiality?.includeDisclaimer ?? true
          },
          dateRange: {
            showOnCover: data.report_settings?.dateRange?.showOnCover ?? true,
            format: data.report_settings?.dateRange?.format || 'full',
            customFormat: data.report_settings?.dateRange?.customFormat || 'MMMM DD, YYYY'
          },
          customSections: data.report_settings?.customSections || []
        }
        
        setSettings(mappedSettings)
      } else {
        initializeWithTenantData()
      }
    } catch (error) {
      console.error('Error loading tenant settings:', error)
      initializeWithTenantData()
    } finally {
      setLoading(false)
    }
  }

  const initializeWithTenantData = () => {
    if (currentTenant) {
      setSettings({
        template: 'professional',
        branding: {
          companyName: currentTenant.name || '',
          logo: '',
          phone: '',
          email: '',
          website: '',
          address: '',
          primaryColor: '#3B82F6',
          accentColor: '#10B981'
        },
        confidentiality: {
          enabled: true,
          level: 'confidential',
          customText: '',
          showWatermark: false,
          includeDisclaimer: true
        },
        dateRange: {
          showOnCover: true,
          format: 'full',
          customFormat: 'MMMM DD, YYYY'
        },
        customSections: []
      })
    }
  }

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (!currentTenant || !currentTenant.id) {
      console.error('Current tenant is not available for logo upload')
      alert('Error: Tenant information not available. Please refresh and try again.')
      return
    }

    setUploading(true)
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${currentTenant.id}-logo-${Date.now()}.${fileExt}`
      const filePath = `company-assets/${currentTenant.id}/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('company-assets')
        .upload(filePath, file)

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('company-assets')
        .getPublicUrl(filePath)

      setSettings(prev => ({
        ...prev,
        branding: {
          ...prev.branding,
          logo: publicUrl
        }
      }))
    } catch (error) {
      console.error('Error uploading logo:', error)
      alert('Error uploading logo. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleSave = async () => {
    if (!currentTenant?.id) {
      alert('Error: Tenant information not available. Please refresh and try again.')
      return
    }

    setSaving(true)
    try {
      // Save to existing tenant table structure
      const { error } = await supabase
        .from('tenants')
        .update({
          report_settings: {
            template: settings.template,
            confidentiality: settings.confidentiality,
            dateRange: settings.dateRange,
            customSections: settings.customSections
          },
          logo_url: settings.branding.logo,
          company_address: settings.branding.address,
          phone: settings.branding.phone,
          email: settings.branding.email,
          website: settings.branding.website,
          primary_color: settings.branding.primaryColor,
          accent_color: settings.branding.accentColor
        })
        .eq('id', currentTenant.id)

      if (error) throw error

      onSave(settings)
      onClose()
    } catch (error) {
      console.error('Error saving settings:', error)
      alert('Failed to save settings. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const addCustomSection = () => {
    const newSection = {
      id: Date.now().toString(),
      title: 'New Section',
      type: 'text',
      content: '',
      fields: []
    }
    setSettings(prev => ({
      ...prev,
      customSections: [...prev.customSections, newSection]
    }))
  }

  const updateCustomSection = (sectionId, updates) => {
    setSettings(prev => ({
      ...prev,
      customSections: prev.customSections.map(section =>
        section.id === sectionId ? { ...section, ...updates } : section
      )
    }))
  }

  const removeCustomSection = (sectionId) => {
    setSettings(prev => ({
      ...prev,
      customSections: prev.customSections.filter(section => section.id !== sectionId)
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Export Settings</h2>
            <p className="text-sm text-slate-600 mt-1">Customize your PDF reports and branding</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 px-6">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : (
            <>
              {/* Template Selection */}
              {activeTab === 'template' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-slate-900 mb-4">Choose Template</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {templates.map(template => (
                        <div
                          key={template.id}
                          className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                            settings.template === template.id
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => setSettings(prev => ({ ...prev, template: template.id }))}
                        >
                          <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg mb-3 flex items-center justify-center relative overflow-hidden">
                            <div 
                              className="absolute inset-0 opacity-20"
                              style={{ backgroundColor: template.color }}
                            />
                            <Eye className="w-8 h-8 text-slate-400" />
                          </div>
                          <h4 className="font-semibold text-slate-900 mb-1">{template.name}</h4>
                          <p className="text-xs text-slate-600 mb-3">{template.description}</p>
                          <div className="flex flex-wrap gap-1">
                            {template.features.map(feature => (
                              <span
                                key={feature}
                                className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded"
                              >
                                {feature}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Branding */}
              {activeTab === 'branding' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-slate-900">Company Branding</h3>
                  
                  {/* Logo Upload */}
                  <div className="bg-slate-50 rounded-xl p-6">
                    <label className="block text-sm font-medium text-slate-700 mb-3">Company Logo</label>
                    <div className="flex items-center space-x-4">
                      {settings.branding.logo && (
                        <div className="w-20 h-20 border rounded-lg overflow-hidden bg-white">
                          <img 
                            src={settings.branding.logo} 
                            alt="Logo" 
                            className="w-full h-full object-contain" 
                          />
                        </div>
                      )}
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          id="logo-upload"
                        />
                        <label
                          htmlFor="logo-upload"
                          className="flex items-center space-x-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-white cursor-pointer transition-colors"
                        >
                          {uploading ? (
                            <div className="animate-spin w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full" />
                          ) : (
                            <Upload className="w-4 h-4" />
                          )}
                          <span>{uploading ? 'Uploading...' : 'Upload Logo'}</span>
                        </label>
                        <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 5MB</p>
                      </div>
                    </div>
                  </div>

                  {/* Company Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
                      <input
                        type="text"
                        value={settings.branding.companyName}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, companyName: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your Company Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Website</label>
                      <input
                        type="url"
                        value={settings.branding.website}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, website: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://yourcompany.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={settings.branding.email}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, email: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="contact@yourcompany.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={settings.branding.phone}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, phone: e.target.value }
                        }))}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                    <textarea
                      value={settings.branding.address}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, address: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                      placeholder="123 Main St, City, State 12345"
                    />
                  </div>

                  {/* Color Scheme */}
                  <div>
                    <h4 className="text-md font-medium text-slate-900 mb-4">Color Scheme</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Primary Color</label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="color"
                            value={settings.branding.primaryColor}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              branding: { ...prev.branding, primaryColor: e.target.value }
                            }))}
                            className="w-12 h-10 border border-slate-300 rounded-lg cursor-pointer"
                          />
                          <input
                            type="text"
                            value={settings.branding.primaryColor}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              branding: { ...prev.branding, primaryColor: e.target.value }
                            }))}
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Accent Color</label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="color"
                            value={settings.branding.accentColor}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              branding: { ...prev.branding, accentColor: e.target.value }
                            }))}
                            className="w-12 h-10 border border-slate-300 rounded-lg cursor-pointer"
                          />
                          <input
                            type="text"
                            value={settings.branding.accentColor}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              branding: { ...prev.branding, accentColor: e.target.value }
                            }))}
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Legal & Privacy */}
              {activeTab === 'legal' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-slate-900">Legal & Privacy Settings</h3>

                  {/* Confidentiality Level */}
                  <div className="bg-slate-50 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-slate-900">Document Confidentiality</h4>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.confidentiality.enabled}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            confidentiality: { ...prev.confidentiality, enabled: e.target.checked }
                          }))}
                          className="mr-2 h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-700">Enable confidentiality markings</span>
                      </label>
                    </div>

                    {settings.confidentiality.enabled && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {confidentialityLevels.map(level => (
                            <label
                              key={level.value}
                              className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                                settings.confidentiality.level === level.value
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="confidentiality"
                                value={level.value}
                                checked={settings.confidentiality.level === level.value}
                                onChange={(e) => setSettings(prev => ({
                                  ...prev,
                                  confidentiality: { ...prev.confidentiality, level: e.target.value }
                                }))}
                                className="sr-only"
                              />
                              <div className="font-medium text-slate-900">{level.label}</div>
                              <div className="text-sm text-slate-600 mt-1">{level.description}</div>
                            </label>
                          ))}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Custom Legal Text</label>
                          <textarea
                            value={settings.confidentiality.customText}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              confidentiality: { ...prev.confidentiality, customText: e.target.value }
                            }))}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            rows="4"
                            placeholder="Add custom legal disclaimers, copyright notices, or confidentiality statements..."
                          />
                        </div>

                        <div className="flex items-center space-x-6">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={settings.confidentiality.showWatermark}
                              onChange={(e) => setSettings(prev => ({
                                ...prev,
                                confidentiality: { ...prev.confidentiality, showWatermark: e.target.checked }
                              }))}
                              className="mr-2 h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-slate-700">Add watermark to pages</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={settings.confidentiality.includeDisclaimer}
                              onChange={(e) => setSettings(prev => ({
                                ...prev,
                                confidentiality: { ...prev.confidentiality, includeDisclaimer: e.target.checked }
                              }))}
                              className="mr-2 h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-slate-700">Include legal disclaimer</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Date Range Display */}
                  <div className="bg-slate-50 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-slate-900">Date Range Display</h4>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.dateRange.showOnCover}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            dateRange: { ...prev.dateRange, showOnCover: e.target.checked }
                          }))}
                          className="mr-2 h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-700">Show date range on cover</span>
                      </label>
                    </div>

                    {settings.dateRange.showOnCover && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Date Format</label>
                          <div className="space-y-2">
                            {dateFormats.map(format => (
                              <label
                                key={format.value}
                                className="flex items-center p-3 border rounded-lg hover:bg-white cursor-pointer transition-colors"
                              >
                                <input
                                  type="radio"
                                  name="dateFormat"
                                  value={format.value}
                                  checked={settings.dateRange.format === format.value}
                                  onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    dateRange: { ...prev.dateRange, format: e.target.value }
                                  }))}
                                  className="mr-3 h-4 w-4 text-blue-600 border-slate-300"
                                />
                                <div>
                                  <div className="font-medium text-slate-900">{format.label}</div>
                                  <div className="text-sm text-slate-600">{format.example}</div>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>

                        {settings.dateRange.format === 'custom' && (
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Custom Format</label>
                            <input
                              type="text"
                              value={settings.dateRange.customFormat}
                              onChange={(e) => setSettings(prev => ({
                                ...prev,
                                dateRange: { ...prev.dateRange, customFormat: e.target.value }
                              }))}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="YYYY-MM-DD"
                            />
                            <p className="text-xs text-slate-500 mt-1">Use standard date formatting tokens</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Custom Sections */}
              {activeTab === 'sections' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-slate-900">Custom Sections</h3>
                    <button
                      onClick={addCustomSection}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Section</span>
                    </button>
                  </div>

                  <div className="space-y-4">
                    {settings.customSections.map(section => (
                      <div key={section.id} className="border border-slate-200 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <input
                            type="text"
                            value={section.title}
                            onChange={(e) => updateCustomSection(section.id, { title: e.target.value })}
                            className="text-lg font-medium text-slate-900 bg-transparent border-none focus:outline-none focus:ring-0 p-0"
                            placeholder="Section Title"
                          />
                          <button
                            onClick={() => removeCustomSection(section.id)}
                            className="text-red-600 hover:text-red-800 transition-colors p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Section Type</label>
                            <select
                              value={section.type}
                              onChange={(e) => updateCustomSection(section.id, { type: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="text">Text Content</option>
                              <option value="table">Data Table</option>
                              <option value="keyvalue">Key-Value Pairs</option>
                              <option value="chart">Chart/Graph</option>
                            </select>
                          </div>
                        </div>

                        {section.type === 'text' && (
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-slate-700 mb-2">Content</label>
                            <textarea
                              value={section.content || ''}
                              onChange={(e) => updateCustomSection(section.id, { content: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              rows="4"
                              placeholder="Enter section content..."
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    {settings.customSections.length === 0 && (
                      <div className="text-center py-12 text-slate-500">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>No custom sections added yet.</p>
                        <p className="text-sm">Click "Add Section" to create custom content for your reports.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-slate-200 bg-slate-50">
          <div className="text-sm text-slate-600">
            <span className="font-medium">Note:</span> Changes will apply to all future PDF exports
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{saving ? 'Saving...' : 'Save Settings'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExportSettingsModal